import ContentImage from "@/components/ContentImage";
import { Link } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import GuideLearnMore from "@/components/guides/GuideLearnMore";
import { Button } from "@/components/ui/button";
import { Users, Search, ScanFace, Hash, Shirt, ShieldCheck, Download, Smartphone, ArrowRight, Check } from "lucide-react";
import heroImage from "@/assets/for-participants-hero.jpg";
import participantImage from "@/assets/participant-finding-photos.jpg";
import { PARTICIPANT_FAQ } from "@/content/forParticipants/faq";

const ForParticipants = () => {
  const searchMethods = [
    {
      icon: Hash,
      title: "Search by Bib Number",
      description: "Enter your race bib number and instantly find every photo where you appear. Our OCR technology reads bibs automatically from every shot.",
    },
    {
      icon: ScanFace,
      title: "Search by Selfie",
      description: "Upload a selfie and our facial recognition AI matches your face across the entire event gallery. Find photos you didn't even know existed.",
    },
    {
      icon: Shirt,
      title: "Search by Colour",
      description: "Filter by jersey or clothing colour — perfect for team events, cycling pelotons, or motorsport races where bib numbers aren't visible.",
    },
  ];

  const benefits = [
    {
      icon: Smartphone,
      title: "Works on Any Device",
      description: "Browse and purchase your race photos from your phone, tablet, or desktop. Our galleries are fully responsive and optimised for mobile.",
    },
    {
      icon: Download,
      title: "Instant Digital Delivery",
      description: "Download your high-resolution, unwatermarked photos immediately after purchase. No waiting, no delays.",
    },
    {
      icon: ShieldCheck,
      title: "Secure Checkout",
      description: "Pay with confidence through our enterprise-grade secure checkout. Your payment details are always protected.",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <AppHeader variant="marketing" />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="Marathon runner celebrating at finish line checking phone for race photos" className="w-full h-full object-cover" width={1920} height={1080} />
          <div className="absolute inset-0 bg-gradient-to-r from-hawk-navy/95 via-hawk-navy/85 to-hawk-navy/60" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl text-white">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
              <Users size={16} className="text-hawk-orange" />
              <span className="text-sm font-medium">For Participants</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-6">
              Find Your Race Photos <span className="text-gradient-primary">In Seconds</span>
            </h1>
            <p className="text-lg md:text-xl text-white/80 mb-8 max-w-2xl">
              No more scrolling through thousands of photos to find yourself. Use your bib number, upload a selfie, or filter by jersey colour — and let AI do the rest.
            </p>
            <Link to="/find-your-photos">
              <Button variant="hero" size="lg" className="group">
                Find Your Photos
                <ArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How to Find Your Photos */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
              Three Smart Ways to <span className="text-hawk-orange">Find Your Photos</span>
            </h2>
            <p className="text-lg text-muted-foreground">
              Photohawk uses cutting-edge AI to match you to your photos — no manual searching required.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {searchMethods.map((method) => (
              <div key={method.title} className="bg-card rounded-2xl p-8 shadow-lg text-center hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center mx-auto mb-5">
                  <method.icon className="text-white" size={32} />
                </div>
                <h3 className="text-xl font-display font-bold mb-3">{method.title}</h3>
                <p className="text-muted-foreground">{method.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Visual Showcase */}
      <section className="py-20 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              {/* A real Photohawk Studio screenshot (src/content/media), listed in the image sitemap (src/lib/seo/pages.ts). */}
              <ContentImage name="selfie-search-results" sizes="(min-width: 768px) 560px, 100vw" className="block h-auto w-full rounded-2xl shadow-xl" />
              <div className="absolute -bottom-4 -right-4 bg-gradient-to-r from-blue-500 to-cyan-400 text-white px-5 py-3 rounded-lg font-semibold shadow-lg">
                AI-Powered Search
              </div>
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-display font-bold mb-6">
                Your Race. Your Moment. <span className="text-hawk-orange">Found Instantly.</span>
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  You've just crossed the finish line. The adrenaline is still pumping, and you want to relive that moment. With Photohawk, you don't have to wait days or scroll through thousands of photos.
                </p>
                <p>
                  Simply enter your bib number or take a quick selfie, and our AI instantly pulls up every photo of you from the entire event — start line, on course, and finish. It's that simple.
                </p>
                <p className="text-foreground font-medium">
                  Over 185 million people have already been detected across Photohawk-powered events. Join them and find your race day memories.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
              A Better Way to Get Your <span className="text-hawk-orange">Race Photos</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-hawk-orange to-hawk-gold flex items-center justify-center mx-auto mb-5">
                  <benefit.icon className="text-white" size={32} />
                </div>
                <h3 className="text-xl font-display font-bold mb-3">{benefit.title}</h3>
                <p className="text-muted-foreground">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={participantImage} alt="Participants finding their event photos" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-hawk-navy/85" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-8">Photos From Events Around the World</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
              {[
                { value: "75M+", label: "Photos Available" },
                { value: "185M+", label: "People Detected" },
                { value: "10K+", label: "Events Covered" },
                { value: "29", label: "Countries" },
              ].map((stat) => (
                <div key={stat.label}>
                  <div className="text-3xl md:text-4xl font-display font-bold text-hawk-orange">{stat.value}</div>
                  <div className="text-sm text-white/60">{stat.label}</div>
                </div>
              ))}
            </div>
            <Link to="/find-your-photos">
              <Button variant="hero" size="lg" className="group">
                Find Your Photos Now
                <ArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ-style content for SEO */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-12">
              Common Questions from <span className="text-hawk-orange">Participants</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {PARTICIPANT_FAQ.map((faq) => (
                <div key={faq.q} className="bg-card rounded-xl p-6 shadow-lg">
                  <h3 className="font-display font-bold mb-2">{faq.q}</h3>
                  <p className="text-muted-foreground text-sm">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <GuideLearnMore slugs={["find-your-race-photos", "face-recognition-event-photos"]} />

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-500 to-cyan-400">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
            Ready to Find Your Race Photos?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Search across thousands of events and find your moments in seconds.
          </p>
          <Link to="/find-your-photos">
            <Button size="lg" className="bg-hawk-navy text-white hover:bg-hawk-navy/90 group text-lg px-8">
              Search Photos Now
              <ArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ForParticipants;
